"use client";

import { Clock3, Search, Sparkles, Trash2, X } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";
import { SearchDiscoveryContent } from "@/components/search/search-discovery-content";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { useLanguage } from "@/hooks/useLanguage";
import { useSearchHistory } from "@/hooks/useSearchHistory";
import { useUnifiedSearch } from "@/hooks/useUnifiedSearch";
import { SEARCH_RECOMMENDED_KEYWORDS } from "@/lib/entity-aliases";
import { analyzeSearchQuery, normalizeSearchQuery } from "@/lib/search/unified-search";
import { cn } from "@/lib/utils";

function formatSats(price: number) {
	return `${(Number(price || 0) / 1000).toFixed(3)} Sats`;
}

function getCollapsedRecentQuery(query: string) {
	const trimmed = query.trim();
	if (trimmed.length <= 12) return trimmed;
	return `${trimmed.slice(0, 5)}...${trimmed.slice(-3)}`;
}

interface DesktopHeaderSearchProps {
	onExpandedChange?: (expanded: boolean) => void;
}

export function DesktopHeaderSearch({
	onExpandedChange,
}: DesktopHeaderSearchProps) {
	const { language } = useLanguage();
	const router = useRouter();
	const inputRef = useRef<HTMLInputElement>(null);
	const anchorRef = useRef<HTMLDivElement>(null);
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState("");
	const [showOnlyExactAddressMarks, setShowOnlyExactAddressMarks] = useState(false);
	const { history, addHistory, clearHistory } = useSearchHistory();
	const labels = useMemo(
		() => ({
			unsupportedEvm:
				language === "zh"
					? "暂不支持 EVM 地址，请输入 ICP 身份 ID、BTC 地址或符文关键词。"
					: "EVM addresses are not supported yet.",
			btcNotFound:
				language === "zh"
					? "未找到与该 BTC 地址精确匹配的用户。"
					: "No user matched this BTC address.",
			failed:
				language === "zh" ? "搜索失败，请稍后重试。" : "Search failed. Please try again later.",
		}),
		[language],
	);
	const {
		analysis,
		exactToken,
		tokenResults,
		addressResult,
		addressMarkResults,
		directMatch,
		errorMessage,
		loading,
	} =
		useUnifiedSearch(query, { labels, enabled: open });

	useEffect(() => {
		onExpandedChange?.(open);
	}, [onExpandedChange, open]);

	useEffect(() => {
		if (!open) return;
		const timer = window.setTimeout(() => inputRef.current?.focus(), 20);
		return () => window.clearTimeout(timer);
	}, [open]);

	const submitToSearchPage = (value: string) => {
		const normalized = normalizeSearchQuery(value);
		if (!normalized) {
			router.push("/search");
			setOpen(false);
			return;
		}

		const nextAnalysis = analyzeSearchQuery(normalized);
		addHistory(normalized, nextAnalysis.intent);
		router.push(`/search?q=${encodeURIComponent(normalized)}`);
		setOpen(false);
	};

	const applyInlineSearch = (value: string) => {
		const normalized = normalizeSearchQuery(value);
		setQuery(normalized);
		if (!normalized) return;
		const nextAnalysis = analyzeSearchQuery(normalized);
		addHistory(normalized, nextAnalysis.intent);
		setOpen(true);
	};

	const openDirectAddress = () => {
		if (!addressResult?.id) return;
		addHistory(analysis.query, analysis.intent);
		if (analysis.intent === "btc-address") {
			router.push(
				`/address/${encodeURIComponent(addressResult.id)}?btc=${encodeURIComponent(analysis.query)}`,
			);
		} else {
			router.push(`/address/${encodeURIComponent(addressResult.id)}`);
		}
		setOpen(false);
	};
	const visibleAddressMarkResults =
		directMatch?.source === "address-mark" && addressMarkResults.length === 1
			? []
			: addressMarkResults
					.filter((item) =>
						showOnlyExactAddressMarks ? item.matchKind === "exact" : true,
					)
					.slice(0, 6);

	return (
		<Popover open={open} onOpenChange={setOpen}>
					<PopoverAnchor asChild>
						<div
							ref={anchorRef}
							className="relative hidden w-full xl:block"
						>
						<Search className="pointer-events-none absolute left-4 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
						<Input
							ref={inputRef}
							value={query}
						onFocus={() => setOpen(true)}
						onClick={() => setOpen(true)}
						onChange={(event) => setQuery(event.target.value)}
						onKeyDown={(event) => {
							if (event.key === "Enter") {
								event.preventDefault();
								submitToSearchPage(query);
							}
							if (event.key === "Escape") {
								setOpen(false);
							}
						}}
						placeholder={
							language === "zh"
								? "搜索符文 / 身份ID / BTC地址 / 俚语 / 名称"
								: "Search runes, identity IDs, BTC addresses, aliases, or names"
						}
							className={cn(
								"h-11 rounded-full border-black/10 bg-white/90 pl-11 pr-11 text-sm shadow-sm transition-all duration-200 ease-out dark:border-white/10 dark:bg-[#17191d]",
								open &&
									"h-12 bg-white shadow-[0_16px_44px_rgba(15,23,42,0.12)] dark:bg-[#17191d] dark:shadow-[0_16px_44px_rgba(0,0,0,0.32)]",
							)}
						/>
						{query ? (
							<button
								type="button"
								onClick={() => {
									setQuery("");
									inputRef.current?.focus();
								}}
								className="absolute right-3 top-1/2 z-10 -translate-y-1/2 rounded-full p-1 text-muted-foreground transition-colors hover:bg-black/[0.05] hover:text-foreground dark:hover:bg-white/[0.06]"
								aria-label={language === "zh" ? "清空搜索内容" : "Clear search"}
							>
								<X className="h-4 w-4" />
							</button>
						) : null}
						{open && !query ? (
							<button
								type="button"
								onClick={() => setOpen(false)}
							className="absolute right-3 top-1/2 z-10 -translate-y-1/2 rounded-full p-1 text-muted-foreground transition-colors hover:bg-black/[0.05] hover:text-foreground dark:hover:bg-white/[0.06]"
							aria-label={language === "zh" ? "关闭搜索" : "Close search"}
						>
							<X className="h-4 w-4" />
						</button>
					) : null}
				</div>
			</PopoverAnchor>
				<PopoverContent
					align="center"
					sideOffset={10}
					className="w-[880px] rounded-[28px] border-black/10 bg-[#fbfcfd] p-0 shadow-[0_28px_90px_rgba(15,23,42,0.16)] data-[state=closed]:duration-150 data-[state=open]:duration-200 data-[side=bottom]:slide-in-from-top-1 dark:border-white/10 dark:bg-[#101217]"
					onInteractOutside={(event) => {
						const target = event.target as Node | null;
						if (target && anchorRef.current?.contains(target)) {
							event.preventDefault();
						}
					}}
				>
				<div className="border-b border-black/5 px-5 py-3 dark:border-white/10">
					<div className="flex items-center justify-between">
						<div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
							<Clock3 className="h-3.5 w-3.5" />
							<span>{language === "zh" ? "即时结果" : "Instant Results"}</span>
						</div>
						<Button
							type="button"
							variant="ghost"
							size="sm"
							className="h-8 rounded-full px-3 text-xs"
							onClick={() => submitToSearchPage(query)}
						>
							{language === "zh" ? "进入完整搜索页" : "Open Full Search"}
						</Button>
					</div>
				</div>

					<div className="grid max-h-[70vh] gap-0 overflow-hidden lg:grid-cols-[1.02fr_0.98fr]">
						<div className="space-y-4 overflow-y-auto p-4">
						{errorMessage ? (
							<div className="rounded-2xl border border-amber-500/20 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:bg-amber-500/10 dark:text-amber-100">
								{errorMessage}
							</div>
						) : null}

						{!query.trim() ? (
								<div className="space-y-3">
									<div className="rounded-2xl border border-black/5 bg-white px-4 py-4 dark:border-white/10 dark:bg-[#17191d]">
										<div className="mb-3 flex items-center justify-between">
											<div className="flex items-center gap-2">
												<Clock3 className="h-4 w-4 text-muted-foreground" />
												<div className="text-sm font-semibold">
													{language === "zh" ? "最近搜索" : "Recent Searches"}
												</div>
											</div>
											{history.length > 0 ? (
												<Button
													type="button"
													variant="ghost"
													size="sm"
													className="h-8 rounded-full px-3 text-xs"
													onClick={clearHistory}
												>
													<Trash2 className="mr-1 h-3.5 w-3.5" />
													{language === "zh" ? "清空历史" : "Clear"}
												</Button>
											) : null}
										</div>
										<div className="flex flex-wrap gap-2">
										{history.length > 0 ? (
											<TooltipProvider>
												{history.slice(0, 8).map((item) => (
													<Tooltip key={`${item.query}-${item.createdAt}`}>
														<TooltipTrigger asChild>
															<Button
																type="button"
																variant="outline"
																size="sm"
																className="h-8 rounded-full border-black/10 px-3 dark:border-white/10"
																onClick={() => applyInlineSearch(item.query)}
															>
																{getCollapsedRecentQuery(item.query)}
															</Button>
														</TooltipTrigger>
														<TooltipContent>
															<p className="max-w-[320px] break-all">{item.query}</p>
														</TooltipContent>
													</Tooltip>
												))}
											</TooltipProvider>
										) : (
											<p className="text-sm text-muted-foreground">
												{language === "zh"
													? "搜索记录会保存在这里，方便快速重搜。"
													: "Recent searches will appear here for quick access."}
											</p>
										)}
									</div>
								</div>
									<div className="rounded-2xl border border-black/5 bg-white px-4 py-4 dark:border-white/10 dark:bg-[#17191d]">
										<div className="mb-3 flex items-center gap-2">
											<Sparkles className="h-4 w-4 text-muted-foreground" />
											<div className="text-sm font-semibold">
												{language === "zh" ? "推荐搜索" : "Recommended Search"}
											</div>
										</div>
										<div className="flex flex-wrap gap-2">
											{SEARCH_RECOMMENDED_KEYWORDS.map((sample) => (
												<Button
													key={sample}
													type="button"
												variant="outline"
												size="sm"
												className="h-8 rounded-full border-black/10 px-3 dark:border-white/10"
												onClick={() => setQuery(sample)}
											>
												{sample}
											</Button>
											))}
										</div>
									</div>
									<div className="rounded-2xl border border-black/5 bg-white px-4 py-4 shadow-sm dark:border-white/10 dark:bg-[#17191d]">
										<SearchDiscoveryContent
											compact={true}
											showHotCommunities={false}
											showMarketPulse={false}
											showMoreTools={false}
										/>
									</div>
								</div>
							) : (
								<div className="space-y-3">
									{addressResult ? (
									<button
										type="button"
										onClick={openDirectAddress}
										className="flex w-full items-center justify-between rounded-2xl border border-black/5 bg-white px-4 py-4 text-left transition-colors hover:bg-black/[0.02] dark:border-white/10 dark:bg-[#17191d] dark:hover:bg-white/[0.03]"
									>
										<div className="min-w-0">
											<div className="truncate text-sm font-semibold">
												{addressResult.id}
											</div>
											<div className="truncate text-xs text-muted-foreground">
												{directMatch?.entityType === "account"
													? language === "zh"
														? `俚语直达: ${directMatch.alias}`
														: `Alias match: ${directMatch.alias}`
													: directMatch?.source === "address-mark"
													? language === "zh"
														? `地址标记匹配: ${directMatch.matchedValue || addressResult.id}`
														: `Address mark: ${directMatch.matchedValue || addressResult.id}`
													: analysis.intent === "principal"
													? language === "zh"
														? "识别为 ICP 身份 ID"
														: "Recognized as ICP identity ID"
													: language === "zh"
														? "BTC 地址归属"
														: "BTC address owner"}
											</div>
										</div>
										<span className="text-xs font-medium text-light-primary dark:text-dark-primary">
											{language === "zh" ? "打开地址页" : "Open"}
										</span>
									</button>
								) : null}

								{visibleAddressMarkResults.length > 0 ? (
									<div className="space-y-2.5">
										<div className="flex items-center justify-between gap-2 px-1">
											<div className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
												{language === "zh" ? "地址标记匹配" : "Address Mark Matches"}
											</div>
											<Button
												type="button"
												variant={showOnlyExactAddressMarks ? "default" : "outline"}
												size="sm"
												className="h-7 rounded-full px-2.5 text-[11px]"
												onClick={() =>
													setShowOnlyExactAddressMarks((current) => !current)
												}
											>
												{language === "zh" ? "精确" : "Exact"}
											</Button>
										</div>
										{visibleAddressMarkResults.map((item) => (
											<button
												key={`${item.address}-${item.matchedField}-${item.matchedValue}`}
												type="button"
												onClick={() => {
													addHistory(analysis.query, analysis.intent);
													router.push(`/address/${encodeURIComponent(item.address)}`);
													setOpen(false);
												}}
												className="flex w-full items-center justify-between rounded-2xl border border-black/5 bg-white px-4 py-3 text-left transition-colors hover:bg-black/[0.02] dark:border-white/10 dark:bg-[#17191d] dark:hover:bg-white/[0.03]"
											>
												<div className="min-w-0 flex-1">
													<div className="flex items-center gap-2">
														{item.color ? (
															<span
																className="h-2.5 w-2.5 shrink-0 rounded-full"
																style={{ backgroundColor: item.color }}
															/>
														) : null}
														<div className="truncate text-sm font-semibold">
															{item.displayName}
														</div>
													</div>
													<div className="truncate font-mono text-[11px] text-muted-foreground">
														{item.address}
													</div>
													<div className="truncate text-xs text-muted-foreground">
														<span className="mr-2 inline-flex rounded-full bg-black/5 px-2 py-0.5 dark:bg-white/10">
															{item.matchKind === "exact"
																? language === "zh"
																	? "精确"
																	: "Exact"
																: language === "zh"
																	? "包含"
																	: "Partial"}
														</span>
														{language === "zh" ? "匹配" : "Matched"}: {item.matchedValue}
													</div>
													{item.groupNames.length > 0 || item.tags.length > 0 ? (
														<div className="mt-2 flex flex-wrap gap-1.5">
															{item.groupNames.slice(0, 1).map((groupName) => (
																<span
																	key={`${item.address}-${groupName}`}
																	className="rounded-full border border-black/10 px-2 py-0.5 text-[10px] text-muted-foreground dark:border-white/10"
																>
																	{language === "zh" ? "分组" : "Group"}: {groupName}
																</span>
															))}
															{item.tags.slice(0, 2).map((tag) => (
																<span
																	key={`${item.address}-${tag}`}
																	className="rounded-full border border-black/10 px-2 py-0.5 text-[10px] text-muted-foreground dark:border-white/10"
																>
																	#{tag}
																</span>
															))}
														</div>
													) : null}
												</div>
												<span className="text-xs font-medium text-light-primary dark:text-dark-primary">
													{language === "zh" ? "打开" : "Open"}
												</span>
											</button>
										))}
									</div>
								) : null}

								{exactToken ? (
									<Link
										href={`/plaza/token/${exactToken.id}`}
										onClick={() => {
											addHistory(analysis.query, analysis.intent);
											setOpen(false);
										}}
										className="flex items-center gap-3 rounded-2xl border border-black/5 bg-white px-4 py-4 transition-colors hover:bg-black/[0.02] dark:border-white/10 dark:bg-[#17191d] dark:hover:bg-white/[0.03]"
									>
										<Image
											src={`https://images.odin.fun/token/${exactToken.id}`}
											alt={exactToken.name}
											width={42}
											height={42}
											className="rounded-full"
										/>
										<div className="min-w-0 flex-1">
											<div className="truncate text-sm font-semibold">
												{exactToken.ticker} #{exactToken.id}
											</div>
											<div className="truncate text-xs text-muted-foreground">
												{directMatch?.entityType === "token"
													? `${exactToken.name} · ${
															language === "zh" ? "俚语直达" : "Alias match"
														}: ${directMatch.alias}`
													: exactToken.name}
											</div>
										</div>
										<div className="text-xs font-medium">{formatSats(exactToken.price)}</div>
									</Link>
								) : null}

								<div className="space-y-2.5">
									{loading && tokenResults.length === 0 ? (
										Array.from({ length: 4 }).map((_, index) => (
											<div
												key={index}
												className="h-16 animate-pulse rounded-2xl bg-black/[0.04] dark:bg-white/[0.06]"
											/>
										))
									) : tokenResults.length > 0 ? (
										tokenResults.slice(0, 6).map((token) => (
											<Link
												key={token.id}
												href={`/plaza/token/${token.id}`}
												onClick={() => {
													addHistory(analysis.query, analysis.intent);
													setOpen(false);
												}}
												className="flex items-center gap-3 rounded-2xl border border-black/5 bg-white px-4 py-3 transition-colors hover:bg-black/[0.02] dark:border-white/10 dark:bg-[#17191d] dark:hover:bg-white/[0.03]"
											>
												<Image
													src={`https://images.odin.fun/token/${token.id}`}
													alt={token.name}
													width={38}
													height={38}
													className="rounded-full"
												/>
												<div className="min-w-0 flex-1">
													<div className="truncate text-sm font-semibold">{token.ticker}</div>
													<div className="truncate text-xs text-muted-foreground">
														{token.name}
													</div>
												</div>
												<div className="text-right text-xs font-medium">
													{formatSats(token.price)}
												</div>
											</Link>
										))
									) : (
										<div
											className={cn(
												"rounded-2xl border border-dashed border-black/10 px-4 py-6 text-sm text-muted-foreground dark:border-white/10",
												query ? "block" : "hidden",
											)}
										>
											{language === "zh"
												? "没有找到直接匹配结果，你可以进入完整搜索页继续筛选。"
												: "No direct match found. Open the full search page to continue."}
										</div>
									)}
								</div>
							</div>
						)}
					</div>

					<div className="hidden overflow-y-auto border-l border-black/5 p-4 dark:border-white/10 lg:block">
						<SearchDiscoveryContent
							compact={true}
							showHotCommunities={false}
							showMarketPulse={true}
							showMoreTools={true}
						/>
					</div>
				</div>
			</PopoverContent>
		</Popover>
	);
}
